import { Component, OnInit } from '@angular/core';
import { NotificationService } from 'app/modules/shared/services/notification.service';
import { ToastService } from 'app/modules/shared/services/toast.service';
import { ClientAlertTypeModel } from '../../interfaces/notification';
import { CurrentUserStateService } from 'app/modules/shared/services/current-user-state.service';
import { ImpersonationService } from 'app/modules/shared/services/impersonation.service';
import {
    ICellClickEventInfo,
    ICustomTableInfoExtend,
} from 'app/modules/shared/components/custom-mat-table/custom-mat-table.component';
import { ITableColumns } from 'app/modules/reports/interfaces/reporting-data';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';
import { CustomMatTableService } from 'app/modules/shared/services/custom-mat-table.service';

@Component({
    selector: 'app-company-notifications',
    templateUrl: './company-notifications.component.html',
    styleUrls: ['./company-notifications.component.scss'],
})
export class CompanyNotificationsComponent implements OnInit {
    constructor(
        private notificationService: NotificationService,
        private toast: ToastService,
        private currentUserService: CurrentUserStateService,
        private impersonationService: ImpersonationService,
        private _customMatTableService: CustomMatTableService
    ) {}

    isPowerUser: boolean;
    notifications: ClientAlertTypeModel[];
    isSuperUser = false;
    AlertValueTypes = [
        '%LOAN_NUMBER',
        '%HIERARCHY_IDENTIFIER',
        '%PRODUCT_TYPE',
        '%GL_ACCOUNT_NUMBER',
    ];

    columnInfoList: ITableColumns[];
    customTableInfo: ICustomTableInfoExtend;
    searchTerm: string;
    action: NoDataActionType;
    isActiveCheckboxStatusKey = 'isActiveCheckboxDisabled';

    ngOnInit() {
        this.action = NoDataActionType.pageStart;
        this.isPowerUser = this.impersonationService.isPowerUser();
        if (this.impersonationService.isImpersonatingUser()) this.isSuperUser = false;
        else this.isSuperUser = this.currentUserService.isLBASuperUser();
        this.initiateTableProperties();
        this.getAlertTypes();
    }

    initiateTableProperties() {
        this.columnInfoList = [
            {
                fieldId: 'isActiveSuperUserControl',
                fieldName: 'Enable (SN)',
                dataType: 'input',
                dataInputProperties: {
                    dataInputType: 'checkbox',
                    key: 'isActiveSuperUserControl',
                    valueKey: '',
                    isCellDependentDdl: false,
                    widthPercent: 50,
                    descriptionKey: '',
                },
                fieldOrder: 0,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 80,
                hidden: !this.isSuperUser,
            },
            {
                fieldId: 'isInactive',
                fieldName: 'Enabled',
                dataType: 'input',
                dataInputProperties: {
                    dataInputType: 'checkbox',
                    key: 'isEnableCheckboxActive',
                    valueKey: '',
                    disableKey: this.isActiveCheckboxStatusKey,
                    isCellDependentDdl: false,
                    widthPercent: 50,
                    descriptionKey: '',
                },
                fieldOrder: 0,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 80,
            },
            {
                fieldId: 'alertDescription',
                fieldName: 'Description',
                fieldOrder: 2,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                width: 300,
                dataType: 'string',
            },
        ];

        this.customTableInfo = {
            tableId: 'company-alert-type-table',
            tableHeight: 600,
            hasData: false,
            isDataReady: false,
            columnInfoList: this.columnInfoList,
            dataSet: [],
            searchTerm: this.searchTerm,
            action: this.action,
            title: 'Company Notifications',
            bandColorList: [],
            dataHandleInUiSide: true,
            showSummary: false,
        };
    }

    inputChangeEvent(event: ICellClickEventInfo) {
        let alert = event.cellData as ClientAlertTypeModel;
        let columnInfo = event.columnInfo;

        if (columnInfo.dataInputProperties?.dataInputType === 'checkbox') {
            let isInactiveColId = columnInfo.dataInputProperties?.key;
            if (columnInfo.fieldId === 'isInactive' && isInactiveColId) {
                alert.isInactive = !alert[isInactiveColId];
                this.notificationService.updateClientAlertType(alert).subscribe({
                    next: (data) => {},
                    error: (error) => {
                        throw new Error(error);
                    },
                });
            }

            if (columnInfo.fieldId === 'isActiveSuperUserControl') {
                let isInactiveColInfo = this.columnInfoList.find(
                    (col) => col.fieldId === 'isInactive'
                );

                if (isInactiveColInfo) {
                    this.updateAlertEnabledCheckboxStatus(isInactiveColInfo, alert);
                }

                this.notificationService.updateClientAlertTypeLBAControl(alert).subscribe({
                    next: (data) => {},
                    error: (error) => {
                        throw new Error(error);
                    },
                });
            }
        }
    }

    contentChanged(events: ClientAlertTypeModel[]) {
        if (events?.length > 0) {
            this.columnInfoList.forEach((col) => {
                events.map((event) => {
                    this.updateAlertEnabledCheckboxStatus(col, event);
                });
            });
        }
    }

    updateAlertEnabledCheckboxStatus(col: ITableColumns, cellData: ClientAlertTypeModel) {
        if (col.dataInputProperties?.key && col.fieldId === 'isInactive') {
            cellData[col.dataInputProperties.key] = !cellData.isInactive;
        }

        if (col.dataInputProperties?.disableKey) {
            if (col.fieldId === 'isInactive') {
                cellData[col.dataInputProperties.disableKey] =
                    !cellData['isActiveSuperUserControl'];
            } else {
                cellData[col.dataInputProperties.disableKey] = true;
            }
        }
    }

    getAlertTypes() {
        this.customTableInfo.dataSet = [];
        this.customTableInfo = this._customMatTableService.refreshCustomMatTable(
            this.customTableInfo,
            false,
            false,
            this.action,
            this.searchTerm
        );
        this.notificationService.getClientNotificationTypes().subscribe({
            next: (data) => {
                if (!this.currentUserService.isLBASuperUser) {
                    this.notifications = data.filter(
                        (ntfc) => ntfc.isActiveSuperUserControl === true
                    );
                } else {
                    this.notifications = data;
                }
                this.notifications = this.formatAlerts(this.notifications);

                if (this.notifications?.length > 0) {
                    this.customTableInfo.dataSet = this.notifications;
                    this.customTableInfo = this._customMatTableService.refreshCustomMatTable(
                        this.customTableInfo,
                        true,
                        true,
                        this.action,
                        this.searchTerm
                    );
                } else {
                    this.customTableInfo = this._customMatTableService.refreshCustomMatTable(
                        this.customTableInfo,
                        false,
                        true,
                        this.action,
                        this.searchTerm
                    );
                }
            },
            error: (error) => {
                this.toast.warning('Something went wrong loading this page. Try again later!');
                this.customTableInfo.dataSet = [];
                this.customTableInfo = this._customMatTableService.refreshCustomMatTable(
                    this.customTableInfo,
                    false,
                    true,
                    NoDataActionType.error,
                    this.searchTerm
                );
            },
        });
    }

    formatAlerts(notifications: ClientAlertTypeModel[]): ClientAlertTypeModel[] {
        for (let notification of notifications) {
            if (notification && notification.alertDescription) {
                notification.alertDescription = this.removeAlertPlaceholder(
                    notification.alertDescription
                );
            }
        }

        return notifications;
    }

    removeAlertPlaceholder(description: string): string {
        this.AlertValueTypes.forEach((el) => {
            if (description.includes(el)) {
                let replacementText = '';
                if (el === '%LOAN_NUMBER') {
                    replacementText = 'Loan';
                }
                description = description.replace(el, replacementText);
            }
        });
        return description;
    }

    deleteUserAlert(alertTypeKey: number): void {
        this.notificationService.deleteUserAlertType(alertTypeKey).subscribe(
            (data) => {},
            (error) => {
                throw new Error(error);
            }
        );
    }
}
